import { createPlugin } from '@backstage/core-plugin-api';
import ReactGA from 'react-ga';

const analyticsModuleGA = createPlugin({
  id: "analytics-provider-ga"
});

var __defProp$1 = Object.defineProperty;
var __defNormalProp$1 = (obj, key, value) => key in obj ? __defProp$1(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField$1 = (obj, key, value) => {
  __defNormalProp$1(obj, typeof key !== "symbol" ? key + "" : key, value);
  return value;
};
class DeferredCapture {
  constructor({ defer = false }) {
    /**
     * Queue of deferred hits to be processed when ready. When undefined, hits
     * can safely be sent without delay.
     */
    __publicField$1(this, "queue");
    this.queue = defer ? [] : void 0;
  }
  /**
   * Indicates that deferred capture may now proceed.
   */
  setReady() {
    if (this.queue) {
      this.queue.forEach(this.sendDeferred);
      this.queue = void 0;
    }
  }
  /**
   * Either forwards the pageview directly to GA, or (if configured) enqueues
   * the pageview hit to be captured when ready.
   */
  pageview(path, metadata = {}) {
    if (this.queue) {
      this.queue.push({
        timestamp: Date.now(),
        data: {
          hitType: "pageview",
          page: path,
          ...metadata
        }
      });
      return;
    }
    ReactGA.send({
      hitType: "pageview",
      page: path,
      ...metadata
    });
  }
  /**
   * Either forwards the event directly to GA, or (if configured) enqueues the
   * event hit to be captured when ready.
   */
  event(eventDetails) {
    if (this.queue) {
      this.queue.push({
        timestamp: Date.now(),
        data: {
          ...eventDetails,
          hitType: "event"
        }
      });
      return;
    }
    ReactGA.event(eventDetails);
  }
  /**
   * Sends a given hit to GA, decorated with the correct queue time.
   */
  sendDeferred(hit) {
    ReactGA.send({
      ...hit.data,
      queueTime: Date.now() - hit.timestamp
    });
  }
}

function isVirtualSearchPageViewType(value) {
  return value === "disabled" || value === "only" || value === "both";
}
function parseVirtualSearchPageViewConfig(config) {
  var _a, _b;
  const vspvModeString = config == null ? void 0 : config.getOptionalString("mode");
  return {
    mode: isVirtualSearchPageViewType(vspvModeString) ? vspvModeString : "disabled",
    mountPath: (_a = config == null ? void 0 : config.getOptionalString("mountPath")) != null ? _a : "/search",
    searchQuery: (_b = config == null ? void 0 : config.getOptionalString("searchQuery")) != null ? _b : "query",
    categoryQuery: config == null ? void 0 : config.getOptionalString("categoryQuery")
  };
}

var __defProp = Object.defineProperty;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => {
  __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
  return value;
};
class GoogleAnalytics {
  /**
   * Instantiate the implementation and initialize ReactGA.
   */
  constructor(options) {
    __publicField(this, "cdmConfig");
    __publicField(this, "customUserIdTransform");
    __publicField(this, "capture");
    __publicField(this, "virtualSearchPageView");
    const {
      cdmConfig,
      identity,
      trackingId,
      identityApi,
      userIdTransform = "sha-256",
      scriptSrc,
      virtualSearchPageView,
      testMode,
      debug
    } = options;
    this.cdmConfig = cdmConfig;
    this.virtualSearchPageView = virtualSearchPageView;
    ReactGA.initialize(trackingId, {
      testMode,
      debug,
      gaAddress: scriptSrc,
      titleCase: false
    });
    this.capture = new DeferredCapture({ defer: identity === "required" });
    this.customUserIdTransform = typeof userIdTransform === "function" ? userIdTransform : void 0;
    if (identity !== "disabled" && identityApi) {
      this.setUserFrom(identityApi);
    }
  }
  /**
   * Instantiate a fully configured GA Analytics API implementation.
   */
  static fromConfig(config, options = {}) {
    var _a, _b, _c, _d;
    const trackingId = config.getString("app.analytics.ga.trackingId");
    const scriptSrc = config.getOptionalString("app.analytics.ga.scriptSrc");
    const identity = config.getOptionalString("app.analytics.ga.identity") || "disabled";
    const virtualSearchPageView = parseVirtualSearchPageViewConfig(
      config.getOptionalConfig("app.analytics.ga.virtualSearchPageView")
    );
    const debug = (_a = config.getOptionalBoolean("app.analytics.ga.debug")) != null ? _a : false;
    const testMode = (_b = config.getOptionalBoolean("app.analytics.ga.testMode")) != null ? _b : false;
    const cdmConfig = (_d = (_c = config.getOptionalConfigArray("app.analytics.ga.customDimensionsMetrics")) == null ? void 0 : _c.map((c) => {
      return {
        type: c.getString("type"),
        index: c.getNumber("index"),
        source: c.getString(
          "source"
        ),
        key: c.getString("key")
      };
    })) != null ? _d : [];
    if (identity === "required" && !options.identityApi) {
      throw new Error(
        "Invalid config: identity API must be provided to deps when ga.identity is required"
      );
    }
    return new GoogleAnalytics({
      ...options,
      identity,
      trackingId,
      scriptSrc,
      virtualSearchPageView,
      cdmConfig,
      testMode,
      debug
    });
  }
  /**
   * Primary event capture implementation. Handles core navigate event as a
   * pageview and the rest as custom events. All custom dimensions/metrics are
   * applied as they should be (set on pageview, merged object on events).
   */
  captureEvent(event) {
    var _a, _b;
    const { context, action, subject, value, attributes } = event;
    const customMetadata = this.getCustomDimensionMetrics(context, attributes);
    if (action === "navigate" && context.extension === "App") {
      this.capture.pageview(subject, customMetadata);
      return;
    }
    if (this.virtualSearchPageView.mode !== "disabled" && action === "search") {
      const { mountPath, searchQuery, categoryQuery } = this.virtualSearchPageView;
      const params = new URLSearchParams();
      params.set(searchQuery, subject);
      if (categoryQuery) {
        params.set(categoryQuery, (_b = (_a = context.searchTypes) == null ? void 0 : _a.toString()) != null ? _b : "");
      }
      this.capture.pageview(
        `${mountPath}?${params.toString()}`,
        customMetadata
      );
      if (this.virtualSearchPageView.mode === "only") {
        return;
      }
    }
    this.capture.event({
      category: context.extension || "App",
      action,
      label: subject,
      value,
      ...customMetadata
    });
  }
  /**
   * Returns an object of dimensions/metrics given an Analytics Context and an
   * Event Attributes, e.g. { dimension1: "some value", metric8: 42 }
   */
  getCustomDimensionMetrics(context, attributes = {}) {
    const customDimensionsMetrics = {};
    this.cdmConfig.forEach((config) => {
      const value = config.source === "context" ? context[config.key] : attributes[config.key];
      if (config.type === "metric" && typeof value !== "number") {
        return;
      }
      if (value !== void 0) {
        customDimensionsMetrics[`${config.type}${config.index}`] = value;
      }
    });
    return customDimensionsMetrics;
  }
  /**
   * Sets the GA userId, based on the `userEntityRef` set on the backstage
   * identity loaded from a given Backstage Identity API instance. Because
   * Google forbids sending any PII (including on the userId field), we hash
   * the entire `userEntityRef` on behalf of integrators:
   *
   * - With value `User:default/name`, userId becomes `sha256(User:default/name)`
   *
   * If an integrator wishes to use an alternative hashing mechanism or an
   * entirely different value, they may do so by passing a `userIdTransform`
   * function alongside the `identityApi` to `GoogleAnalytics.fromConfig()`.
   * This function receives the `userEntityRef` as an argument and should
   * resolve to a hashed version of whatever identifier they choose.
   *
   * Note: this feature requires that an integrator has set up a Google
   * Analytics User ID view in the property used to track Backstage.
   */
  async setUserFrom(identityApi) {
    const { userEntityRef } = await identityApi.getBackstageIdentity();
    const userId = await this.getPrivateUserId(userEntityRef);
    ReactGA.set({ userId });
    this.capture.setReady();
  }
  /**
   * Returns a PII-free (according to Google's terms of service) user ID for
   * use in Google Analytics.
   */
  getPrivateUserId(userEntityRef) {
    if (this.customUserIdTransform) {
      return this.customUserIdTransform(userEntityRef);
    }
    return this.hash(userEntityRef);
  }
  /**
   * Simple hash function; relies on web cryptography + the sha-256 algorithm.
   */
  async hash(value) {
    const digest = await window.crypto.subtle.digest(
      "sha-256",
      new TextEncoder().encode(value)
    );
    const hashArray = Array.from(new Uint8Array(digest));
    return hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
  }
}

export { GoogleAnalytics, analyticsModuleGA };
//# sourceMappingURL=index.esm.js.map
